import { useQuery } from '@tanstack/react-query';
import { History } from 'lucide-react';

import type { ChangelogEntry } from '@tasks/contracts';

import { listChangelog } from '../../api/issues';
import { queryKeys } from '../../api/queryKeys';
import { Avatar } from '../../components/Avatar';
import { EmptyState } from '../../components/EmptyState';
import { Skeleton } from '../../components/Skeleton';
import { formatDateTime, groupDateLabel } from '../../lib/format';
import styles from './IssueHistoryTab.module.css';

const FIELD_LABELS: Readonly<Record<string, string>> = {
  status: 'статус',
  assignee: 'исполнитель',
  priority: 'приоритет',
  summary: 'заголовок',
  description: 'описание',
  storyPoints: 'story points',
  dueDate: 'срок',
  labels: 'метки',
  components: 'компоненты',
  sprint: 'спринт',
  epic: 'эпик',
  resolution: 'резолюция',
  rank: 'порядок',
};

function renderValue(value: unknown): string {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.length === 0 ? '—' : value.map((item) => renderValue(item)).join(', ');
  }
  if (typeof value === 'object' && 'name' in value) {
    return renderValue((value as { name: unknown }).name);
  }
  return JSON.stringify(value);
}

/** UC-HIS-01: от новых к старым, группировка «сегодня/вчера/дата». */
export function IssueHistoryTab({ issueKey }: { readonly issueKey: string }) {
  const query = useQuery({
    queryKey: queryKeys.issues.changelog(issueKey),
    queryFn: ({ signal }) => listChangelog(issueKey, { limit: 100, offset: 0 }, signal),
  });

  if (query.isPending) {
    return <Skeleton variant="row" count={5} />;
  }

  const entries = query.data?.items ?? [];
  if (entries.length === 0) {
    return (
      <EmptyState
        compact
        icon={<History size={18} />}
        title="История пуста"
        description="Здесь появятся изменения полей задачи."
      />
    );
  }

  const groups = groupByDay(entries);

  return (
    <div className={styles['tab']}>
      {groups.map((group) => (
        <section key={group.label}>
          <h4 className={styles['groupTitle']}>{group.label}</h4>
          <ul className={styles['list']}>
            {group.entries.map((entry) => (
              <li key={entry.id} className={styles['entry']}>
                <Avatar user={entry.actor} size="xs" />
                <div className={styles['content']}>
                  <p className={styles['line']}>
                    <strong>{entry.actor?.displayName ?? 'Система'}</strong>{' '}
                    <span className={styles['field']}>{FIELD_LABELS[entry.field] ?? entry.field}</span>{' '}
                    <span className={styles['old']}>{renderValue(entry.oldValue)}</span>
                    <span aria-hidden> → </span>
                    <span className={styles['new']}>{renderValue(entry.newValue)}</span>
                  </p>
                  <p className={styles['time']}>{formatDateTime(entry.createdAt)}</p>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

function groupByDay(entries: readonly ChangelogEntry[]): readonly { label: string; entries: readonly ChangelogEntry[] }[] {
  const map = new Map<string, ChangelogEntry[]>();
  for (const entry of entries) {
    const label = groupDateLabel(entry.createdAt);
    const bucket = map.get(label) ?? [];
    bucket.push(entry);
    map.set(label, bucket);
  }
  return [...map.entries()].map(([label, items]) => ({ label, entries: items }));
}
